import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import GaugeValueDisplay from './GaugeValueDisplay';
import {
    hasMetricSources,
    getMetricSourceForRequest,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getFormattedThresholdValue,
    getErrorMessageKey,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';
import { getValueStyles } from './gaugeValueDisplayUtils';
import { getFormattedValue } from '../../../dashboardUtils';

function getThreshold(thresholds) {
    if (_.isEmpty(thresholds)) {
        return {};
    }

    if (!thresholds[0].value) {
        return {
            thresholdErrorKey: 'missingThresholdValue',
        };
    }

    return { threshold: thresholds[0] };
}

function getMetricDataFromSource(metrics, request) {
    const metricData = getMetricSourceForRequest(metrics, request);
    return metricData || {};
}

// eslint-disable-next-line complexity
function getMetricSources(
    metricRequests,
    metrics,
    calculatedMetricConfiguration
) {
    const metricRequestsAreInvalid =
        metricRequests.length === 0 ||
        metricRequests.some((request) => request.metrics.length === 0);

    if (metricRequestsAreInvalid) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSources =
        calculatedMetricConfiguration.requestIdsOfMetricsToCombine
            .map((requestId) => {
                const request = metricRequests.find((x) => x.id === requestId);
                if (!request) {
                    return {};
                }
                return {
                    ...getMetricDataFromSource(metrics, request),
                    metricName: request.metrics[0],
                };
            })
            .filter((source) => !_.isEmpty(source));

    if (metricSources.length !== metricRequests.length) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources(metricSources)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSources };
}

// eslint-disable-next-line complexity
export default function CalculatedGaugeValueDisplayWrapper(props) {
    const { isTime, linkConfig, suffix = '', timeFormat } = props.configuration;
    const { threshold, thresholdErrorKey } = getThreshold(props.thresholds);
    const { metricSources, metricSourceErrors, errorKey } = getMetricSources(
        props.metricRequests,
        props.metrics,
        props.calculatedMetricConfiguration
    );

    if (errorKey || thresholdErrorKey) {
        const errorMessageKey = getErrorMessageKey(errorKey, thresholdErrorKey);
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const gaugeValuesForEachSource = metricSources.map((source) => {
        const value = source.metricSeries[0]?.[source.metricName];
        return value !== null && value !== undefined ? value : null;
    });

    // eslint-disable-next-line no-new-func
    const calculateMetricFunction = new Function(
        props.calculatedMetricConfiguration.calculateFunction
    );

    const value = !gaugeValuesForEachSource.includes(null)
        ? calculateMetricFunction(...gaugeValuesForEachSource)
        : undefined;

    const formatter = isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const gaugeValue = getFormattedValue(value, suffix, formatter, timeFormat);
    const thresholdValue = getFormattedThresholdValue(
        threshold,
        formatter,
        suffix,
        timeFormat
    );

    return (
        <GaugeValueDisplay
            gaugeValue={gaugeValue}
            linkConfig={linkConfig}
            metricRequest={props.metricRequests[0]}
            openLinksInNewTab={props.openLinksInNewTab}
            thresholdLabel={threshold?.label}
            thresholdValue={thresholdValue}
            valueStyles={getValueStyles(gaugeValue, threshold)}
        />
    );
}

CalculatedGaugeValueDisplayWrapper.propTypes = {
    calculatedMetricConfiguration: PropTypes.instanceOf(Object).isRequired,
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    metricRequests: PropTypes.instanceOf(Array).isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
